import { FC, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import { useSnackbar } from 'notistack'
import { notificacaoService } from '@/services/notificacaoService'
import type { PreferenciasNotificacao } from '@/types/notificacao'

/**
 * Preferências de e-mail por categoria (Fase 6, item 23).
 *
 * Só o e-mail é configurável: o aviso na plataforma é o registro do que aconteceu e
 * continua sempre — desligar uma categoria aqui significa "não me mande cópia por
 * e-mail disso", não "não me avise". A cópia é dizer isso na tela, para ninguém achar
 * que silenciou a pendência.
 */

const CATEGORIAS: { chave: keyof PreferenciasNotificacao; titulo: string; detalhe: string }[] = [
  { chave: 'emailPendencias', titulo: 'Pendências', detalhe: 'Solicitações que exigem sua ação, cobranças e lembretes.' },
  { chave: 'emailOperacao', titulo: 'Operação', detalhe: 'Reserva criada, enviada, vencendo, renovação, transferência e análise.' },
  { chave: 'emailFinanceiro', titulo: 'Financeiro', detalhe: 'NF, pagamento, comissão e simulação.' },
]

const PADRAO: PreferenciasNotificacao = { emailPendencias: true, emailOperacao: true, emailFinanceiro: true }

export const PreferenciasNotificacaoCard: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [prefs, setPrefs] = useState<PreferenciasNotificacao>(PADRAO)
  const [salvas, setSalvas] = useState<PreferenciasNotificacao>(PADRAO)
  const [carregando, setCarregando] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const p = await notificacaoService.obterPreferencias()
      setPrefs(p)
      setSalvas(p)
    } catch {
      setErro('Não foi possível carregar suas preferências.')
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const alterado = CATEGORIAS.some((c) => prefs[c.chave] !== salvas[c.chave])

  const salvar = async () => {
    setSalvando(true)
    try {
      const p = await notificacaoService.salvarPreferencias(prefs)
      setPrefs(p)
      setSalvas(p)
      enqueueSnackbar('Preferências salvas', { variant: 'success' })
    } catch {
      enqueueSnackbar('Não foi possível salvar as preferências', { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <NotificationsActiveOutlinedIcon fontSize="small" color="primary" />
        <Typography fontWeight={600}>Avisos por e-mail</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Escolha de que categorias quer receber cópia por e-mail. Os avisos na plataforma
        continuam sempre — aqui você só decide o que chega na sua caixa de entrada.
      </Typography>

      <Divider sx={{ my: 2 }} />

      {carregando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} aria-label="Carregando preferências" />
        </Box>
      ) : erro ? (
        <Alert
          severity="error"
          action={<Button size="small" color="inherit" onClick={() => void carregar()}>Tentar de novo</Button>}
        >
          {erro}
        </Alert>
      ) : (
        <Stack spacing={1}>
          {CATEGORIAS.map((c) => (
            <FormControlLabel
              key={c.chave}
              sx={{ alignItems: 'flex-start', ml: 0 }}
              control={
                <Switch
                  checked={prefs[c.chave]}
                  onChange={(e) => setPrefs((p) => ({ ...p, [c.chave]: e.target.checked }))}
                  disabled={salvando}
                  inputProps={{ 'aria-label': `E-mail de ${c.titulo}` }}
                />
              }
              label={
                <Box sx={{ pt: 0.75 }}>
                  <Typography variant="body2" fontWeight={600}>{c.titulo}</Typography>
                  <Typography variant="caption" color="text.secondary">{c.detalhe}</Typography>
                </Box>
              }
            />
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
            <Button variant="contained" onClick={() => void salvar()} disabled={!alterado || salvando}>
              {salvando ? 'Salvando…' : 'Salvar preferências'}
            </Button>
          </Box>
        </Stack>
      )}
    </Paper>
  )
}
